<template>
  <main id="app">
    <!-- Global Banner System - Positioned above all content -->
    <BannerSystem />
    
    <!-- NEW LOADING SYSTEM: Show loading during bootstrap and initial app loading -->
    <div v-if="isAppReady">
      <DynamicHead />
      <!-- Using the v-slot API on router-view for a fallback -->
      <router-view v-slot="{ Component }">
        <!-- If Component is falsy, show the LoadingModal -->
        <component :is="Component || LoadingModal" />
      </router-view>
    </div>
    
    <!-- Show loading indicator during app bootstrap -->
    <div v-else>
      <DynamicHead />
      <router-view v-slot="{ Component }">
        <component :is="Component || LoadingModal" />
      </router-view>
      
      <!-- Semi-transparent loading overlay - so we can see content underneath -->
      <div v-if="shouldShowAppOverlay" class="app-loading-overlay">
        <SmartLoadingIndicator 
          :message="loadingMessage"
          :progress="loadingProgress"
          :loading-phase="currentLoadingPhase"
          variant="spinner"
          :show-progress="true"
          size="xl"
        />
      </div>
    </div>
    
    <div class="z-50">
      <div v-for="notification in notifications" :key="notification.id">
        <Snackbar :notification="notification" />
      </div>
    </div>
    
    <!-- Demo Mode Banner - Manages itself via banner system -->
    <DemoModeBanner />
    
    <!-- Unified Debug Panel - Always on Top in Development -->
    <UnifiedDebugPanel v-if="isDevelopmentMode" class="debug-panel-overlay" />

    <!-- Idle overlay -->
    <IdleOverlay />
  </main>
</template>

<script setup lang="ts">
// NEW LOADING SYSTEM: Connected to centralized loading store
// Replaces old loading logic with new router-based loading management

import { computed, defineAsyncComponent, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useApplicationStore } from '@/store/application.store';
import { useLoadingStore, LoadingPhase } from '@/store/loading.store';
import { LOADING_FEATURES } from '@/config/loading-features';
import { useGtm } from '@gtm-support/vue-gtm';

const LoadingModal = defineAsyncComponent(() => import('@/components/ui/modals/LoadingModal.vue'));
const Snackbar = defineAsyncComponent(() => import('@/components/ui/modals/SnackbarModal.vue'));
const DynamicHead = defineAsyncComponent(() => import('@/components/app/root/DynamicHead.vue'));
import { onMounted as vueOnMounted } from 'vue';
import { updateChecker } from '@/services/updateChecker';
const BannerSystem = defineAsyncComponent(() => import('@/components/app/root/BannerSystem.vue'));
const SmartLoadingIndicator = defineAsyncComponent(() => import('@/components/loading/SmartLoadingIndicator.vue'));
const UnifiedDebugPanel = defineAsyncComponent(() => import('@/components/debug/UnifiedDebugPanel.vue'));
const DemoModeBanner = defineAsyncComponent(() => import('@/components/app/root/DemoModeBanner.vue'));
const IdleOverlay = defineAsyncComponent(() => import('@/components/loading/IdleOverlay.vue'));
import { useSessionHealthMonitoring } from '@/composables/useSessionHealthMonitoring';
import { useProjectType } from '@/composables/useProjectType';
import { useRoute } from 'vue-router';
import { useCentralizedDemo } from '@/composables/useCentralizedDemo';
import { useAnalytics } from '@/composables/useAnalytics';

const applicationStore = useApplicationStore();

// Initialize analytics tracking - this activates watchers that sync
// projectId and userId to the analytics service whenever they change
useAnalytics({
  trackPageViews: true,
  debug: import.meta.env.DEV,
});
const gtm = useGtm();
const route = useRoute();
const { isPrivateLabel } = useProjectType();

// SIMPLIFIED LOADING SYSTEM: Use ApplicationStore managed by SimplifiedRouterGuard
const isAppReady = computed(() => applicationStore.isAppReady);
const loadingMessage = computed(() => applicationStore.loadingTitle || 'Loading application...');
const loadingProgress = computed(() => 0);
const currentLoadingPhase = computed(() => applicationStore.isAppReady ? 'app_ready' : 'bootstrap');

console.log('✅ [App.vue] Connected to SimplifiedRouterGuard loading system via ApplicationStore');

const notifications = computed(() => applicationStore.notifications);

// On private-label sites, allow certain routes to opt-out of the bootstrap overlay
// by setting meta.skipLoading = true (e.g., Login/Register)
const shouldShowAppOverlay = computed(() => {
  const skipForRoute = Boolean(route.meta?.skipLoading);
  if (isPrivateLabel.value && skipForRoute) return false;
  return true; // default show when app is not ready
});

// Initialize conservative session health monitoring unless in demo
// Note: enableInDevelopment is false - idle warnings are distracting during local dev
const { isDemoMode } = useCentralizedDemo();
if (!isDemoMode.value) {
  useSessionHealthMonitoring({
    enableIdleManagement: true,
    enablePeriodicValidation: false,
    enableRootCauseDetection: import.meta.env.DEV, // off in prod
    enableInDevelopment: false, // Disable idle warnings in development - they're just noise
    // If later enabled, keep it infrequent
    sessionValidationInterval: 30 * 60 * 1000
  });
} else {
  console.log('🛑 [App.vue] Demo mode active - session health monitoring not initialized');
}

// Development mode check - Debug panel always available in dev
const isDevelopmentMode = computed(() => {
  const isDev = import.meta.env.DEV;
  console.log('🔧 [App.vue] Debug panel available:', isDev);
  return isDev;
});

// Initialize the loading system on app mount
onMounted(() => {
  console.log('🚀 [App.vue] App mounted, loading system integration active');
  
  try {
    if (LOADING_FEATURES.NEW_LOADING_SYSTEM || import.meta.env.DEV) {
      const loadingStore = useLoadingStore();
      
      // Start in APP_READY phase by default since app is already mounted
      // The debug panel can override this to BOOTSTRAP for testing
      if (loadingStore.phase === LoadingPhase.BOOTSTRAP) {
        console.log('📱 [App.vue] Starting from bootstrap phase');
      } else {
        console.log('📱 [App.vue] App ready, loading system active');
      }
    }
  } catch (error) {
    console.warn('⚠️ [App.vue] Could not initialize loading system:', error);
  }
});

// Start update checking when app mounts
vueOnMounted(() => {
  try {
    updateChecker.startUpdateChecking();
  } catch (e) {
    console.warn('Failed to start update checker', e);
  }
});

gtm?.trackView('AppVue', 'currentPath');
</script>

<style scoped>
/* Loading overlay - semi-transparent so we can see content underneath */
.app-loading-overlay {
  @apply fixed inset-0 flex items-center justify-center z-50;
  background: rgba(0, 0, 0, 0.3); /* Semi-transparent background */
}

.app-loading-overlay .loading-container {
  @apply max-w-md bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6;
}

/* Data Loading Overlay - Above most content but below debug panel */
:deep(.data-loading-overlay) {
  z-index: 9999 !important;
}

/* Debug Panel - Always on top in dev mode */
:deep(.debug-panel-overlay) {
  z-index: 999999 !important; /* Highest possible z-index */
}

/* Debug panel main container - only target the root container, not tab content */
:deep(.debug-panel-overlay > div:first-child) {
  position: fixed !important;
  bottom: 16px !important;
  right: 16px !important;
  z-index: 999999 !important;
}

/* Debug toggle button - more specific selector */
:deep(.debug-panel-overlay > div:first-child > button:first-child) {
  z-index: 1000000 !important;
}
</style>
